import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Shield,
  Award,
  ArrowLeft,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  Copy,
  Loader2,
  TrendingUp,
  Activity,
  Clock,
  Zap,
  Globe,
} from 'lucide-react';
import PledgeTimeline from '@/components/PledgeTimeline';
import { BreachRecordList, fetchBreachRecords, BREACH_RECORD_NOTE } from '@/components/kypa/BreachRecords';
import { SellerDomainPanel } from '@/components/kypa/SellerDomainPanel';
import { SellerAgentBadge } from '@/components/kypa/SellerAgentBadge';
import { BreachExportButton } from '@/components/kypa/BreachExport';
import { BindingAuthorityBadge, BindingAuthorityCard, useBindingPosture } from '@/components/BindingAuthority';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  Bronze: { text: 'text-amber-500', border: 'border-amber-700/40', bg: 'from-amber-800/20 to-amber-900/5', dot: 'bg-amber-500' },
  Silver: { text: 'text-slate-300', border: 'border-slate-400/40', bg: 'from-slate-400/15 to-slate-500/5', dot: 'bg-slate-300' },
  Gold: { text: 'text-yellow-400', border: 'border-yellow-500/40', bg: 'from-yellow-500/15 to-yellow-600/5', dot: 'bg-yellow-400' },
  Platinum: { text: 'text-cyan-300', border: 'border-cyan-400/40', bg: 'from-cyan-400/15 to-blue-500/10', dot: 'bg-cyan-300' },
};

const FLAG_STYLES = {
  None: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'Clean · No open flags' },
  Yellow: { text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', label: 'Yellow · Minor issue open' },
  Orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', label: 'Orange · Active investigation' },
  Red: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30', label: 'Red · Delisted' },
};

const SIGNAL_LABELS = {
  wallet: 'Wallet signature',
  domain: 'Domain (DNS TXT)',
  github: 'GitHub identity',
  x: 'X (Twitter) identity',
  ens: 'ENS record',
  framework: 'Framework attestation',
  tls: 'TLS certificate',
};

function fmtUsd(n) {
  if (n == null) return '-';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
  return `$${n.toLocaleString()}`;
}

function fmtInt(n) {
  if (n == null) return '-';
  return n.toLocaleString();
}

function shortWallet(w) {
  if (!w) return '-';
  return `${w.slice(0, 6)}…${w.slice(-4)}`;
}

function fmtDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      className="text-gray-500 hover:text-white transition-colors"
      title="Copy"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function StatCard({ label, value, sub, tone = 'default', testid }) {
  const toneCls =
    tone === 'good'
      ? 'text-emerald-400'
      : tone === 'bad'
      ? 'text-red-400'
      : tone === 'accent'
      ? 'text-cyan-300'
      : 'text-white';
  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold font-mono ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function KYPAAgentProfile() {
  const { kypa_id: kypaId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posture, reloadPosture] = useBindingPosture(kypaId);
  const [breach, setBreach] = useState(null);

  useEffect(() => {
    if (kypaId) fetchBreachRecords(kypaId).then(setBreach).catch(() => setBreach(null));
  }, [kypaId]);

  useEffect(() => {
    let canceled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${API}/api/kypa/profile/${encodeURIComponent(kypaId)}`);
        if (r.status === 404) {
          throw new Error(`No KYPA profile found with id "${kypaId}".`);
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!canceled) setProfile(d);
      } catch (err) {
        if (!canceled) setError(err.message);
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    if (kypaId) load();
    return () => {
      canceled = true;
    };
  }, [kypaId]);

  const tier = profile ? TIER_STYLES[profile.pledge_tier] || TIER_STYLES.Bronze : TIER_STYLES.Bronze;
  const flag = profile ? FLAG_STYLES[profile.warning_flag] || FLAG_STYLES.None : FLAG_STYLES.None;
  const baseScanUrl = profile ? `https://basescan.org/address/${profile.wallet_address}` : '#';

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="kypa-agent-profile-page">
      <Helmet>
        <title>{profile ? `${profile.agent_name} · KYPA Profile | Kojiru` : 'KYPA Agent Profile | Kojiru'}</title>
        <meta
          name="description"
          content={
            profile
              ? `${profile.agent_name}: pledged ${fmtUsd(profile.pledge_amount_usdc)} on Base L2. (ACS)™ ${profile.acs_score}. Public breach records listed on this page.`
              : 'Public KYPA profile for a pledged AI agent on Base L2.'
          }
        />
      </Helmet>
      <Navbar />

      {/* Back nav */}
      <div className="pt-24 px-6" data-testid="kypa-agent-back-nav">
        <div className="container mx-auto max-w-6xl">
          <Link
            to="/kypa/directory"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-400 hover:text-white"
            data-testid="kypa-agent-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to directory
          </Link>
        </div>
      </div>

      {loading && (
        <div
          className="flex items-center justify-center py-32"
          data-testid="kypa-agent-loading"
        >
          <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="py-16 px-6" data-testid="kypa-agent-error">
          <div className="container mx-auto max-w-3xl">
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-8 text-center">
              <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
              <h1 className="text-2xl font-semibold text-white mb-2">Profile not found</h1>
              <p className="text-sm text-gray-400 mb-6">{error}</p>
              <Link to="/kypa/directory">
                <Button className="bg-red-500 hover:bg-red-600 text-white gap-2" data-testid="kypa-agent-error-back">
                  <ArrowLeft className="w-4 h-4" /> Back to directory
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && profile && (
        <>
          {/* Header */}
          <section
            className={`py-10 px-6 border-b border-white/5 bg-gradient-to-br ${tier.bg}`}
            data-testid="kypa-agent-header"
          >
            <div className="container mx-auto max-w-6xl">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${tier.border} bg-black/30 text-xs font-bold uppercase tracking-widest ${tier.text}`}
                      data-testid="kypa-agent-tier-badge"
                    >
                      <Award className="w-3.5 h-3.5" /> {profile.pledge_tier}
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-black/30 text-xs uppercase tracking-widest text-gray-300">
                      <span>{profile.framework}</span>
                    </div>
                    <div
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold uppercase tracking-widest ${flag.bg} ${flag.text} ${flag.border}`}
                      data-testid="kypa-agent-flag"
                    >
                      {flag.label}
                    </div>
                    <BindingAuthorityBadge posture={posture} />
                  </div>
                  <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-2 break-words"
                    data-testid="kypa-agent-name"
                  >
                    {profile.agent_name}
                  </motion.h1>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400 font-mono">
                    <span data-testid="kypa-agent-id">{profile.kypa_id}</span>
                    <CopyButton value={profile.kypa_id} testid="kypa-agent-id-copy" />
                    <span className="text-gray-600">·</span>
                    <a
                      href={baseScanUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-red-300 hover:text-red-200"
                      data-testid="kypa-agent-basescan-link"
                    >
                      {shortWallet(profile.wallet_address)} <ExternalLink className="w-3 h-3" />
                    </a>
                    <CopyButton value={profile.wallet_address} testid="kypa-agent-wallet-copy" />
                  </div>
                  <div className="mt-3"><SellerAgentBadge agent={profile} testid="kypa-agent-seller-badge" /></div>
                  {profile.description && (
                    <p className="mt-4 text-sm text-gray-300 leading-relaxed max-w-2xl">
                      {profile.description}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2 shrink-0">
                  {profile.endpoint_url && (
                    <a
                      href={profile.endpoint_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="kypa-agent-endpoint-link"
                    >
                      <Button className="bg-red-500 hover:bg-red-600 text-white gap-2 w-full md:w-auto">
                        <Globe className="w-4 h-4" /> Visit endpoint
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  )}
                  <a
                    href={baseScanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="kypa-agent-basescan-cta"
                  >
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/5 gap-2 w-full md:w-auto"
                    >
                      <Shield className="w-4 h-4" /> View on BaseScan
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Stat grid */}
          <SellerDomainPanel profile={profile} onUpdate={(d) => setProfile((p) => ({ ...p, ...d }))} />
          <section className="py-8 px-6 border-b border-white/5" data-testid="kypa-agent-stats">
            <div className="container mx-auto max-w-6xl">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard
                  label="Pledged USDC"
                  value={fmtUsd(profile.pledge_amount_usdc)}
                  sub={`${profile.pledge_tier} tier`}
                  tone="good"
                  testid="kypa-agent-stat-pledge"
                />
                <StatCard
                  label="(ACS)™ Score"
                  value={fmtInt(profile.acs_score)}
                  sub="300 – 850 band"
                  testid="kypa-agent-stat-acs"
                />
                <StatCard
                  label="30d Volume"
                  value={fmtUsd(profile.transaction_volume_30d)}
                  sub={`${fmtInt(profile.successful_transactions)} lifetime txns`}
                  tone="accent"
                  testid="kypa-agent-stat-vol30"
                />
                <StatCard
                  label="Lifetime Volume"
                  value={fmtUsd(profile.transaction_volume_lifetime)}
                  testid="kypa-agent-stat-volLifetime"
                />
                <StatCard
                  label="Breach Records"
                  value={fmtInt(breach?.total ?? 0)}
                  sub="Public evidence"
                  tone={breach?.total ? 'bad' : 'good'}
                  testid="kypa-agent-stat-breach"
                />
                <StatCard
                  label="30d Uptime"
                  value={`${(profile.uptime_30d ?? 100).toFixed(1)}%`}
                  testid="kypa-agent-stat-uptime"
                />
              </div>
            </div>
          </section>

          {/* Details grid */}
          <section className="py-10 px-6" data-testid="kypa-agent-details">
            <div className="container mx-auto max-w-6xl grid grid-cols-1 lg:grid-cols-3 gap-6">              {/* LEFT: identity + operator */}
              <div className="lg:col-span-2 space-y-6">
                {/* Verification signals */}
                <div
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
                  data-testid="kypa-agent-verification-card"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-400" />
                      <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold">
                        Verification stack
                      </h2>
                    </div>
                    <div className="text-xs font-mono text-emerald-300">
                      {profile.verification_score} · {profile.verification_signals.length} signals
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {profile.verification_signals.map((sig) => (
                      <div
                        key={sig}
                        className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-sm"
                        data-testid={`kypa-agent-signal-${sig}`}
                      >
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                        <span className="text-gray-200">{SIGNAL_LABELS[sig] || sig}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
                    Every signal is a cryptographic or DNS attestation: never a form field. Kojiru
                    is the registrar; it does not vouch for the operator&rsquo;s intent, only for
                    the fact of the pledge on-chain.
                  </p>
                </div>

                {/* Operator identity */}
                <div
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
                  data-testid="kypa-agent-operator-card"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Activity className="w-4 h-4 text-cyan-300" />
                    <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold">
                      Operator identity
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Public handle</div>
                      <div className="font-mono text-gray-200 break-all" data-testid="kypa-agent-operator-identity">
                        {profile.operator_identity || '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Framework</div>
                      <div className="text-gray-200 capitalize">{profile.framework}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Endpoint</div>
                      <div className="text-gray-200 font-mono break-all">
                        {profile.endpoint_url ? (
                          <a
                            href={profile.endpoint_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-red-300 hover:text-red-200 inline-flex items-center gap-1"
                          >
                            {profile.endpoint_url}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Wallet (Base)</div>
                      <div className="font-mono text-gray-200 break-all">
                        <a
                          href={baseScanUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-300 hover:text-red-200 inline-flex items-center gap-1"
                        >
                          {profile.wallet_address}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>
                  {profile.categories && profile.categories.length > 0 && (
                    <div className="mt-4">
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Categories</div>
                      <div className="flex flex-wrap gap-1.5" data-testid="kypa-agent-categories">
                        {profile.categories.map((c) => (
                          <span
                            key={c}
                            className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: pledge history + audit */}
              <div className="space-y-6">
                {/* Pledge and breach records */}
                <div
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
                  data-testid="kypa-agent-pledge-card"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-4 h-4 text-red-400" />
                    <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold">
                      Pledge &amp; breach records
                    </h2>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Current pledge</span>
                      <span className={`font-mono ${tier.text}`}>
                        {fmtUsd(profile.pledge_amount_usdc)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Tier</span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${tier.dot}`} />
                        <span className={tier.text}>{profile.pledge_tier}</span>
                      </span>
                    </div>
                    <div className="h-px bg-white/10 my-2" />
                    <div data-testid="kypa-agent-breach-feed">
                      <div className="text-xs text-gray-400 mb-2">All breach records ({fmtInt(breach?.total ?? 0)})</div>
                      <BreachRecordList records={breach?.records} testid="kypa-agent-breach-list" />
                      <BreachExportButton kypaId={profile.kypa_id} />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-4 leading-relaxed">
                    {BREACH_RECORD_NOTE} The pledge is a public commitment signal. Kojiru never holds or routes funds,
                    is not insurance, and does not promise repayment to any party.
                  </p>
                </div>

                <BindingAuthorityCard tier={profile.pledge_tier} kypaId={profile.kypa_id} wallet={profile.wallet_address} posture={posture} onPostureSaved={reloadPosture} />

                {/* Timeline */}
                <div
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
                  data-testid="kypa-agent-timeline-card"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-4 h-4 text-cyan-300" />
                    <h2 className="text-sm uppercase tracking-widest text-gray-400 font-semibold">
                      Directory record
                    </h2>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Verified since</span>
                      <span className="font-mono text-gray-200">{fmtDate(profile.verified_since)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Enrolled</span>
                      <span className="font-mono text-gray-200">{fmtDate(profile.created_at)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Status</span>
                      <span
                        className={`text-xs font-bold uppercase tracking-widest ${
                          profile.status === 'active' ? 'text-emerald-400' : 'text-red-400'
                        }`}
                      >
                        {profile.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Trust footer */}
                <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                    <div className="text-xs uppercase tracking-widest font-bold text-emerald-300">
                      Directory data source
                    </div>
                  </div>
                  <p className="text-[12px] text-gray-300 leading-relaxed">
                    Every field on this profile is derived from a signed on-chain event or a DNS /
                    cryptographic attestation. If a value here disagrees with Base mainnet, Base
                    wins.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="pb-12 px-6" data-testid="kypa-agent-timeline-section">
            <div className="container mx-auto max-w-6xl">
              <PledgeTimeline profile={profile} />
            </div>
          </section>

          <Footer />
        </>
      )}
    </div>
  );
}
